<template>
	<div class="dt-range">
		<MyDateTimePicker
			class="dt-range__field"
			width="210px"
			:value="value.start || ''"
			:rules="[startRule]"
			:use-seconds="useSeconds"
			:display-seconds="startDisplaySeconds"
			placeholder="開始時間"
			@input="onStartInput"
		/>

		<span class="dt-range__sep">~</span>

		<MyDateTimePicker
			ref="endPicker"
			class="dt-range__field"
			width="210px"
			:value="value.end"
			:min="startDate"
			:rules="[endRule]"
			:allow-forever="allowForever"
			:use-seconds="useSeconds"
			:display-seconds="endDisplaySeconds"
			placeholder="結束時間"
			@input="onEndInput"
		/>
	</div>
</template>

<script>
import MyDateTimePicker from '@/components/DateTimePicker.vue'

export default {
	name: 'MyDateTimeRangePicker',
	components: { MyDateTimePicker },
	props: {
		// { start: 'YYYY-MM-DD HH:mm', end: 'YYYY-MM-DD HH:mm' | null }；end 為 null 代表永久
		value: {
			required: false,
			type: Object,
			default: () => ({ start: '', end: '' }),
		},
		allowForever: {
			required: false,
			type: Boolean,
			default: true,
		},
		// 是否「可手選」秒數；預設 false，向下傳遞給兩個選擇器（值格式 HH:mm / HH:mm:ss）
		useSeconds: {
			required: false,
			type: Boolean,
			default: false,
		},
		// 「只顯示」用的固定秒數，開始/結束各自指定（例如公告 '00' / '59'）：
		// 僅影響呈現、不寫入值，亦不可手選
		startDisplaySeconds: {
			required: false,
			type: String,
			default: '',
		},
		endDisplaySeconds: {
			required: false,
			type: String,
			default: '',
		},
	},
	model: {
		prop: 'value',
		event: 'input',
	},
	computed: {
		forever() {
			return this.value.end === null
		},
		startDate() {
			return this.value.start ? this.value.start.split(' ')[0] : undefined
		},
	},
	watch: {
		// 結束的規則相依於開始，但 vuetify 只在欄位自身的 value 變動時重驗，
		// 改開始不會連動結束。不主動重驗會兩頭失準：既有的錯誤解不開（送出鈕卡在 disabled），
		// 原本合法的組合改壞了也擋不下來。
		'value.start'() {
			this.$refs.endPicker?.validate()
		},
	},
	methods: {
		emitValue(start, end) {
			this.$emit('input', { start, end })
		},
		onStartInput(val) {
			this.emitValue(val, this.value.end)
		},
		onEndInput(val) {
			// val 可能為 null（使用者在結束選擇器內選了「永久」）
			this.emitValue(this.value.start, val)
		},
		startRule() {
			return !!this.value.start || '請選擇開始時間'
		},
		// 比對讀 value 而非規則參數：欄位傳進來的是「補過 displaySeconds 的顯示字串」，
		// 拿它跟未補秒數的 value.start 比對會因格式不一致而失準。
		endRule() {
			if (this.forever) {
				return true
			}
			if (!this.value.end) {
				return '請選擇結束時間'
			}
			// 相等視為合法：本元件的值只到分鐘，同一分鐘的起訖代表「涵蓋該分鐘」，
			// 實際涵蓋到的秒數由使用端決定（見 displaySeconds 與送出時的補秒）。
			if (this.value.start && this.value.end < this.value.start) {
				return '結束不可早於開始'
			}
			return true
		},
	},
}
</script>

<style lang="scss" scoped>
.dt-range {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;

	&__sep {
		color: rgba(0, 0, 0, 0.4);
		font-weight: bold;
	}
}
</style>
